// Pure helpers for the pre-2018 PM revenue-gap issue's decide flow
// (Baker 2026-07-13). The mortgage-pre-2018-pm-revenue-gap
// recommendation said "mark verified after setting fee" — but the loan's
// record page had no editor and the rail had no input, so the item was a
// full dead end. The rail now renders a dollars input and sends
// params.amount_earned_cents; the backend writes it onto the loan
// (audited, 60s-undoable) and resolves the issue. The escape ("We can't
// determine this fee") resolves durably via keep-current — the loan's
// amount stays blank so revenue roll-ups never count a made-up number.
//
// Same pattern + conventions as client-since-issue.ts / program-type-
// issue.ts. Root-safe (imports nothing) so it's unit-tested from
// tests/web/.

// Minimal structural subset of CleanupIssueSummary this module reads.
export interface PmFeeIssueLike {
  detectorSlug: string;
}

// Issues whose "verify" carries an amount-earned figure to the backend.
export function isPmFeeIssue(issue: PmFeeIssueLike): boolean {
  return issue.detectorSlug === "mortgage-pre-2018-pm-revenue-gap";
}

// Dollars input → positive amount, up to 2 decimals, ≤ $10M (the
// backend's sanity ceiling). Accepts "1500", "1,500", "1500.50",
// "$1500" — advisors paste from spreadsheets.
export function parseFeeDollars(s: string): number | null {
  const cleaned = s.trim().replace(/^\$/, "").replace(/,/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n <= 0 || n > 10_000_000) return null;
  return n;
}

export function isValidFeeInput(s: string): boolean {
  return parseFeeDollars(s) !== null;
}

// Dollars string → integer cents for the decide POST.
export function feeInputToCents(s: string): number | null {
  const dollars = parseFeeDollars(s);
  return dollars === null ? null : Math.round(dollars * 100);
}

function formatFee(s: string): string {
  const dollars = parseFeeDollars(s);
  return dollars === null
    ? s.trim()
    : `$${dollars.toLocaleString("en-US", {
        minimumFractionDigits: dollars % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      })}`;
}

// Plain-language before→after for the amount-carrying verify (UX rule
// #3). Entering the amount IS the action — the backend 400s without it.
export function pmFeeConsequence(
  issue: PmFeeIssueLike,
  amount: string,
): string | null {
  if (!isPmFeeIssue(issue)) return null;
  if (amount && isValidFeeInput(amount)) {
    return `Sets the amount earned (Stewardship's fee) on this Private Money loan to ${formatFee(
      amount,
    )} and resolves this issue — it then counts in the dashboard's PM revenue tile. Audited; reversible for 60 seconds.`;
  }
  return "Enter the amount Stewardship earned on this loan first — setting it is the whole action here. Use Baker's historical records. Genuinely undeterminable? Use “We can't determine this fee” below — that answer sticks too.";
}

// Copy for the "can't determine" escape button + its consequence line.
// No durable marker column exists on mortgage_loans (and no migration is
// warranted for it) — the answer is durable anyway because a resolved
// issue is never reopened by re-detection.
export const PM_FEE_UNKNOWN_LABEL = "We can't determine this fee";

export function pmFeeUnknownConsequence(): string {
  return "Records that this loan's fee can't be determined from the records we have — the item resolves for the whole team and won't come back. The loan's amount earned stays blank, so revenue totals never include a guess. Reversible for 60 seconds (undo reopens the item), and it can be reopened any time after.";
}
